import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Briefcase,
  PlusCircle,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  Star,
  Trash2,
  RotateCcw,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  Edit,
  Users,
  Building2,
  MapPin,
  Sparkles,
  Archive,
  AlertCircle,
} from "lucide-react";
import { careersService } from "@/api/careersService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

/**
 * ============================================================
 * ADMIN CAREERS MANAGEMENT (/admin/careers)
 * ============================================================
 * Central career positions management center.
 * Implements listing, KPIs, search, filtering, display reordering,
 * instant status switching (Draft, Published, Closed, Archived),
 * featured toggling, soft deletion, and candidate tracking.
 */
export default function AdminCareers() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedWorkplace, setSelectedWorkplace] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'Published' | 'Draft' | 'Closed' | 'Archived' | 'trash'

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      const [jobsData, deptsData] = await Promise.all([
        careersService.getAllJobsAdmin({
          status: statusFilter,
          department: selectedDepartment,
          workplace: selectedWorkplace,
          search: searchTerm,
          includeDeleted: statusFilter === "trash",
        }),
        careersService.getDepartments(),
      ]);
      setJobs(jobsData || []);
      setDepartments(deptsData || []);
    } catch (err) {
      console.error("[AdminCareers] Fetch error:", err);
      toast.error(err.message || "Failed to load career listings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, selectedDepartment, selectedWorkplace]);

  // Handle live search
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) return jobs;
    const term = searchTerm.toLowerCase().trim();
    return jobs.filter(
      (j) =>
        j.title?.toLowerCase().includes(term) ||
        j.slug?.toLowerCase().includes(term) ||
        j.departments?.name?.toLowerCase().includes(term)
    );
  }, [jobs, searchTerm]);

  // Overall KPIs
  const totalPositions = jobs.length;
  const publishedPositions = jobs.filter((j) => j.status === "Published" && !j.deleted_at).length;
  const draftPositions = jobs.filter((j) => j.status === "Draft" && !j.deleted_at).length;
  const closedPositions = jobs.filter((j) => j.status === "Closed" && !j.deleted_at).length;
  const totalApplications = jobs.reduce((sum, j) => sum + (j.applications_count || 0), 0);

  // Status Change handler
  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await careersService.updateJobStatus(jobId, newStatus);
      toast.success(`Position status updated to "${newStatus}".`);
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, status: newStatus } : j))
      );
    } catch (err) {
      toast.error(err.message || "Failed to update position status.");
    }
  };

  // Toggle Featured handler
  const handleToggleFeatured = async (job) => {
    try {
      const nextState = !job.is_featured;
      await careersService.toggleFeatured(job.id, nextState);
      toast.success(`Position "${job.title}" ${nextState ? "marked as featured" : "unfeatured"}.`);
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, is_featured: nextState } : j))
      );
    } catch (err) {
      toast.error(err.message || "Failed to update featured flag.");
    }
  };

  // Soft Delete handler
  const handleSoftDelete = async (job) => {
    if (!window.confirm(`Move position "${job.title}" to trash? It will be hidden from the public careers page.`)) return;
    try {
      await careersService.softDeleteJob(job.id);
      toast.success(`Position "${job.title}" moved to trash.`);
      fetchCatalog();
    } catch (err) {
      toast.error(err.message || "Failed to delete position.");
    }
  };

  // Restore handler
  const handleRestore = async (job) => {
    try {
      await careersService.restoreJob(job.id);
      toast.success(`Position "${job.title}" restored.`);
      fetchCatalog();
    } catch (err) {
      toast.error(err.message || "Failed to restore position.");
    }
  };

  // Reorder handler
  const handleMoveOrder = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= filteredJobs.length) return;

    const copy = [...filteredJobs];
    const [moved] = copy.splice(index, 1);
    copy.splice(targetIndex, 0, moved);

    setJobs(copy);

    try {
      await careersService.reorderJobs(copy);
      toast.success("Display ordering updated.");
    } catch (err) {
      toast.error(err.message || "Failed to save new display ordering.");
      fetchCatalog();
    }
  };

  return (
    <div className="space-y-6 select-none">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-semibold uppercase tracking-wider mb-2">
            <Briefcase className="w-3.5 h-3.5" />
            <span>Talent Acquisition</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            Career Management
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs font-mono font-normal">
              {filteredJobs.length} roles
            </Badge>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 font-medium">
            Manage open positions, review applications, and control public role visibility.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/admin/careers/applications"
            className="rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 hover:text-slate-900 text-xs font-semibold px-4 h-10 inline-flex items-center gap-2 transition-colors"
          >
            <Users className="w-4 h-4 text-cyan-600" />
            <span>Applications Pipeline</span>
          </Link>

          <Button
            onClick={() => navigate("/admin/careers/new")}
            className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold gap-2 h-10 px-5 shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Post New Role</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-mono">{totalPositions}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1 font-medium">Total Roles</div>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-emerald-700 font-mono">{publishedPositions}</div>
          <div className="text-[11px] text-emerald-600 uppercase tracking-wider mt-1 font-medium">Published Live</div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-amber-700 font-mono">{draftPositions}</div>
          <div className="text-[11px] text-amber-600 uppercase tracking-wider mt-1 font-medium">Drafts</div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-blue-700 font-mono">{closedPositions}</div>
          <div className="text-[11px] text-blue-600 uppercase tracking-wider mt-1 font-medium">Closed</div>
        </div>

        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-cyan-700 font-mono">{totalApplications}</div>
          <div className="text-[11px] text-cyan-600 uppercase tracking-wider mt-1 font-medium">Total Applicants</div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-xs">
          <div className="text-2xl sm:text-3xl font-bold text-slate-700 font-mono">
            {jobs.filter((j) => j.is_featured && !j.deleted_at).length}
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1 font-medium">Featured</div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4 shadow-xs">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by role title, slug, or department..."
              className="pl-10 h-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl text-xs focus:bg-white"
            />
          </div>

          {/* Department Filter */}
          <div className="flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
            >
              <option value="all" className="bg-white text-slate-900">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id} className="bg-white text-slate-900">
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Workplace Filter */}
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <select
              value={selectedWorkplace}
              onChange={(e) => setSelectedWorkplace(e.target.value)}
              className="h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
            >
              <option value="all" className="bg-white text-slate-900">All Workplaces</option>
              <option value="Remote" className="bg-white text-slate-900">Remote</option>
              <option value="Hybrid" className="bg-white text-slate-900">Hybrid</option>
              <option value="On-site" className="bg-white text-slate-900">On-site</option>
            </select>
          </div>

          {/* Status Tabs */}
          <div className="flex items-center rounded-xl bg-slate-100 p-1 border border-slate-200 overflow-x-auto text-xs">
            {["all", "Published", "Draft", "Closed", "Archived", "trash"].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={cn(
                  "px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap capitalize",
                  statusFilter === status
                    ? "bg-white text-cyan-700 font-semibold shadow-xs border border-slate-200"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                {status === "all" ? "All Statuses" : status === "trash" ? "Trash" : status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Content */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-16 rounded-2xl bg-slate-100 border border-slate-200" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-slate-200 bg-white p-8 space-y-3 shadow-xs">
          <AlertCircle className="w-8 h-8 text-slate-300 mx-auto" />
          <h3 className="text-base font-semibold text-slate-900">No career positions found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchTerm || selectedDepartment !== "all" || statusFilter !== "all"
              ? "Try adjusting your filters or search criteria."
              : "Create your first open position to begin accepting job applications."}
          </p>
          <Button
            onClick={() => navigate("/admin/careers/new")}
            className="rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-xs font-semibold mt-2"
          >
            Post Position
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="py-3.5 px-4 w-12 text-center">#</th>
                  <th className="py-3.5 px-4">Position Title</th>
                  <th className="py-3.5 px-4">Department</th>
                  <th className="py-3.5 px-4">Type / Location</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-center">Featured</th>
                  <th className="py-3.5 px-4 text-center">Applicants</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {filteredJobs.map((job, idx) => {
                  const isDeleted = Boolean(job.deleted_at);

                  return (
                    <tr
                      key={job.id}
                      className={cn(
                        "hover:bg-slate-50/80 transition-colors",
                        isDeleted && "opacity-60 bg-rose-50/30"
                      )}
                    >
                      {/* Order Controls */}
                      <td className="py-4 px-4 text-center font-mono text-slate-400">
                        {statusFilter === "Published" ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => handleMoveOrder(idx, "up")}
                              className="text-slate-400 hover:text-cyan-600 disabled:opacity-20 transition-colors p-0.5"
                              title="Move Up"
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-[10px]">{job.display_order || idx + 1}</span>
                            <button
                              type="button"
                              disabled={idx === filteredJobs.length - 1}
                              onClick={() => handleMoveOrder(idx, "down")}
                              className="text-slate-400 hover:text-cyan-600 disabled:opacity-20 transition-colors p-0.5"
                              title="Move Down"
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span>{job.display_order || idx + 1}</span>
                        )}
                      </td>

                      {/* Title & Slug */}
                      <td className="py-4 px-4">
                        <div className="font-semibold text-slate-900 text-sm hover:text-cyan-700 transition-colors flex items-center gap-2">
                          <Link to={`/admin/careers/${job.id}/edit`}>{job.title}</Link>
                          {job.is_featured && (
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                          /{job.slug}
                        </div>
                      </td>

                      {/* Department */}
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 border border-slate-200 text-slate-700">
                          {job.departments?.name || "General"}
                        </span>
                      </td>

                      {/* Type & Workplace */}
                      <td className="py-4 px-4">
                        <div className="text-slate-800 font-medium">{job.employment_type}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 text-cyan-600" />
                          <span>{job.workplace_type} ({job.location})</span>
                        </div>
                      </td>

                      {/* Status Dropdown */}
                      <td className="py-4 px-4">
                        {isDeleted ? (
                          <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                            In Trash
                          </Badge>
                        ) : (
                          <select
                            value={job.status}
                            onChange={(e) => handleStatusChange(job.id, e.target.value)}
                            className={cn(
                              "text-xs px-2.5 py-1 rounded-lg border font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500/40 cursor-pointer",
                              job.status === "Published"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : job.status === "Draft"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : job.status === "Closed"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-slate-100 text-slate-600 border-slate-200"
                            )}
                          >
                            <option value="Published" className="bg-white text-slate-900">Published</option>
                            <option value="Draft" className="bg-white text-slate-900">Draft</option>
                            <option value="Closed" className="bg-white text-slate-900">Closed</option>
                            <option value="Archived" className="bg-white text-slate-900">Archived</option>
                          </select>
                        )}
                      </td>

                      {/* Featured Toggle */}
                      <td className="py-4 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleToggleFeatured(job)}
                          disabled={isDeleted}
                          className={cn(
                            "p-1.5 rounded-lg border transition-colors",
                            job.is_featured
                              ? "bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100"
                              : "text-slate-300 border-slate-200 hover:text-slate-600 hover:bg-slate-50"
                          )}
                          title={job.is_featured ? "Featured role" : "Mark as featured"}
                        >
                          <Star className={cn("w-3.5 h-3.5", job.is_featured && "fill-amber-500 text-amber-500")} />
                        </button>
                      </td>

                      {/* Applications Count */}
                      <td className="py-4 px-4 text-center">
                        <Link
                          to={`/admin/careers/${job.id}/applications`}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 transition-colors font-mono font-medium text-xs"
                        >
                          <Users className="w-3 h-3" />
                          <span>{job.applications_count || 0}</span>
                        </Link>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {isDeleted ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestore(job)}
                              className="h-8 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1.5"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span>Restore</span>
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/admin/careers/${job.id}/edit`)}
                                className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                title="Edit Position"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSoftDelete(job)}
                                className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                title="Move to Trash"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
